import React, { useCallback, useEffect, useMemo } from 'react';
import { Field, useField, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { ThreeDots } from 'react-loader-spinner';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import { Input } from '../../../components/common-inputs';
import WithError from '../../../components/with-error/WithError';
import SearchInput from '../../../components/common-inputs/SearchInput';
import UploadFile from '../components/UploadFile';
import CommonButton from '../../../components/common-button/CommonButton';
import { getAccessUsers, loadMoreAccessUsers } from '../../../redux/projects/projectsActions';
import AccessContainers from '../../../components/access-containers/AccessContainers';
import useResonanceThemeOptions from '../../../hooks/useResonanceThemeOptions';
import CommonDropdown from '../../../components/common-dropdown/CommonDropdown';
import useResonanceVisionOptions from '../../../hooks/useResonanceVisionOptions';
import useResonanceStructureOptions from '../../../hooks/useResonanceStructureOptions';
import { RESONANCE_STRUCTURE, RESONANCE_VISIONS } from '../../../constants';
import useResonanceLanguageOptions from '../../../hooks/useResonanceLanguageOptions copy';
import DropdownWithIcons from '../../../components/common-dropdown/DropdownWithIcons';

import scss from './ResonanceCreation.module.scss';

const ProjectForm = ({ handleSubmit }) => {
  const { t } = useTranslation('projects');
  const { restart, change } = useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { values } = useFormState();

  const isAccessUsersLoading = useSelector((state) => state.projects.isAccessUsersLoading);
  const isLoading = useSelector((state) => state.projects.isLoading);
  const { accessUsers, totalAccessUsers } = useSelector((state) => state.projects);
  const resonanceThemeOptions = useResonanceThemeOptions();
  const resonanceVisionOptions = useResonanceVisionOptions();
  const resonanceStructureOptions = useResonanceStructureOptions();
  const resonanceLanguageOptions = useResonanceLanguageOptions();

  const inactiveUsers = useMemo(
    () => accessUsers.filter((user) => !values.activeUsers.some((activeUser) => activeUser.id === user.id)),
    [values, accessUsers],
  );

  const {
    input: { value: dataStructureValue, onChange: changeDataStructure },
  } = useField('dataStructure');

  const {
    input: { onChange: changeVision },
  } = useField('vision');

  const handleRestForm = useCallback(() => {
    restart();
    navigate('/projects/create');
  }, [restart, navigate]);

  const handleActiveUser = useCallback(
    (user) => {
      change('activeUsers', [...values.activeUsers, user]);
    },
    [values, change],
  );

  const handleInactiveUser = useCallback(
    ({ id }) => {
      change(
        'activeUsers',
        values.activeUsers.filter((user) => user.id !== id),
      );
    },
    [values, change],
  );

  const handleSearchUsers = useCallback(
    (newSearch) => {
      change('assignUser', newSearch);
      dispatch(getAccessUsers({ search: newSearch, page: 1 }));
    },
    [change, dispatch],
  );

  const handleLoadMoreUsers = useCallback(
    (newPage) => {
      dispatch(loadMoreAccessUsers({ search: values.assignUser ?? '', page: newPage }));
    },
    [dispatch, values.assignUser],
  );

  const handleDataStructureChange = useCallback(
    (newValue) => {
      changeDataStructure(newValue);

      if (newValue === RESONANCE_STRUCTURE.RESPONDENTS) {
        changeVision(RESONANCE_VISIONS.DEFAULT);
      }
    },
    [changeDataStructure, changeVision],
  );

  useEffect(() => {
    dispatch(getAccessUsers({ search: '', page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form className={scss.form} onSubmit={handleSubmit}>
      <Field name='projectName' autoComplete='project-name'>
        {({ meta, input }) => (
          <WithLabel
            variant={LABEL_STYLES.ALIGNED}
            label={`${t('projectName')}:`}
            isBold
            extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <Input
                type='text'
                size='sm'
                isError={meta.touched && meta.error}
                formProps={input}
                disabled={isLoading}
                autoComplete='project-name'
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='resonanceTheme'>
        {({ input, meta }) => (
          <WithLabel variant={LABEL_STYLES.ALIGNED} label={`${t('theme')}:`} isBold extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <CommonDropdown
                value={input.value}
                options={resonanceThemeOptions}
                onSelect={input.onChange}
                name={input.name}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                isError={meta.touched && meta.error}
                disabled={isLoading}
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='dataStructure'>
        {({ input, meta }) => (
          <WithLabel
            variant={LABEL_STYLES.ALIGNED}
            label={`${t('dataStructure')}:`}
            isBold
            extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <CommonDropdown
                value={input.value}
                options={resonanceStructureOptions}
                onSelect={handleDataStructureChange}
                name={input.name}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                isError={meta.touched && meta.error}
                disabled={isLoading}
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      {dataStructureValue === RESONANCE_STRUCTURE.RESPONDENTS ? (
        <Field name='lang'>
          {({ input, meta }) => (
            <WithLabel variant={LABEL_STYLES.ALIGNED} label={`${t('language')}:`} isBold extraClassName={scss.inputRow}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <DropdownWithIcons
                  value={input.value}
                  options={resonanceLanguageOptions}
                  onSelect={input.onChange}
                  name={input.name}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  isError={meta.touched && meta.error}
                  disabled={isLoading}
                  isEmojiIcons
                />
              </WithError>
            </WithLabel>
          )}
        </Field>
      ) : null}

      {dataStructureValue === RESONANCE_STRUCTURE.MESSAGES ? (
        <Field name='vision'>
          {({ input, meta }) => (
            <WithLabel variant={LABEL_STYLES.ALIGNED} label={`${t('vision')}:`} isBold extraClassName={scss.inputRow}>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <CommonDropdown
                  value={input.value}
                  options={resonanceVisionOptions}
                  onSelect={input.onChange}
                  name={input.name}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  isError={meta.touched && meta.error}
                  disabled={isLoading}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>
      ) : null}

      <Field name='assignUser'>
        {({ input }) => (
          <WithLabel label={t('assignUser')} variant={LABEL_STYLES.ALIGNED} isBold extraClassName={scss.inputRow}>
            <SearchInput
              value={input.value}
              onChange={handleSearchUsers}
              name={input.name}
              size='sm'
              disabled={isLoading}
            />
          </WithLabel>
        )}
      </Field>

      <Field name='activeUsers'>
        {({ input, meta }) => (
          <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <AccessContainers
              onInactive={handleInactiveUser}
              onActive={handleActiveUser}
              inactive={inactiveUsers}
              active={input.value}
              disabled={isLoading}
              isLoading={isAccessUsersLoading}
              searchStr={values.assignUser}
              onLoadMore={handleLoadMoreUsers}
              totalElements={totalAccessUsers}
              inactiveLoadedSize={accessUsers.length}
            />
          </WithError>
        )}
      </Field>

      <Field name='file' type='file'>
        {({ input, meta }) => (
          <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <UploadFile onChange={input.onChange} name={input.name} disabled={isLoading} value={input.value} />
          </WithError>
        )}
      </Field>

      <div className={scss.actions}>
        <CommonButton type='submit' styleColor='primary' onClick={handleSubmit} maxWidth={100} disabled={isLoading}>
          {isLoading ? <ThreeDots height={15} width={40} /> : null}
          {t('create')}
        </CommonButton>
        <CommonButton type='button' styleColor='secondary' onClick={handleRestForm} maxWidth={100} disabled={isLoading}>
          {t('cancel')}
        </CommonButton>
      </div>
    </form>
  );
};

export default ProjectForm;
